import { HOLDINGS_EVENT_TYPE_OPTIONS } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/utils';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { Button, Drawer, Textarea, Title } from '@mantine/core';
import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useWatch } from 'react-hook-form';
import { MantineDateInput } from '@dealstack/frontend/shared/components/mantineForm/MantineDateInput';
import {
	CREATE_HOLDING_EVENT_FORM_FIELD,
	useHoldingEventDrawerController,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/HoldingEventDrawer/useHoldingEventDrawerController.ts';
import { HoldingEventRes, HoldingEventType } from '@dealstack/shared/lib/generated';
import {
	RegularViewTableItemData,
	SimpleRegularViewTableItemData,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/RegularViewTable/useRegularViewTableController.ts';
import {
	HoldingCard,
	HoldingsEventTab,
	HoldingsEventTabs,
	InfoCard,
	ParentDiv,
	SelectTitle,
	UnitsTitle,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/HoldingEventDrawer/styled';
import {
	getDateLabel,
	hasEventType,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/HoldingEventDrawer/utils.ts';
import { FilePreview } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/HoldingEventDrawer/FilePreviewComponent.tsx';
import { EntitySelectComponent as HoldingEntitySelect } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/HoldingEventDrawer/EntitySelectComponent.tsx';
import { PledgeAgainstSelectComponent as PledgedAgainstSelect } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/HoldingEventDrawer/PledgeAgainstSelectComponent';
import { FileUploadComponent } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/HoldingEventDrawer/FileUploadComponent.tsx';
import { browserDateToMidnightUTC } from '@dealstack/frontend/shared/utils/utc.ts';

interface Props {
	holding?: RegularViewTableItemData;
	className?: string;
	opened: boolean;
	onClose: (createdEventId?: string) => void;
	refreshSecuritiesList: () => void;
	existingEvents?: HoldingEventRes[];
	debtHoldings?: SimpleRegularViewTableItemData[];
}

export const CreateHoldingEventDrawer = ({
	holding,
	opened,
	onClose,
	className,
	refreshSecuritiesList,
	existingEvents = [],
	debtHoldings = [],
}: Props) => {
	const tCapManage = useTranslationCapManage();

	const {
		createHoldingsEventForm,
		createHoldingsEventRequest,
		onSubmit,
		setUploadedFile,
		uploadedFile,
		removeFile,
		entitiesSelectOptions,
	} = useHoldingEventDrawerController(refreshSecuritiesList, onClose, holding);

	const [localEntitiesOptions, setLocalEntitiesOptions] = useState(entitiesSelectOptions);

	useEffect(() => {
		setLocalEntitiesOptions(entitiesSelectOptions);
	}, [entitiesSelectOptions]);

	const selectedType = useWatch({
		control: createHoldingsEventForm.control,
		name: CREATE_HOLDING_EVENT_FORM_FIELD.type,
	});

	const availableEventTypes = HOLDINGS_EVENT_TYPE_OPTIONS.filter(
		(option) => !hasEventType(existingEvents, option.value as HoldingEventType)
	);

	const defaultType =
		availableEventTypes.length > 0
			? availableEventTypes[0].value
			: HOLDINGS_EVENT_TYPE_OPTIONS[0].value;

	useMemo(() => {
		if (!selectedType) {
			createHoldingsEventForm.setValue(CREATE_HOLDING_EVENT_FORM_FIELD.type, defaultType);
		}
	}, [createHoldingsEventForm, defaultType, selectedType]);

	const dateLabel = getDateLabel(selectedType, tCapManage);

	const handleTabSelect = (type: HoldingEventType) => {
		if (!hasEventType(existingEvents, type as HoldingEventType)) {
			createHoldingsEventForm.setValue(CREATE_HOLDING_EVENT_FORM_FIELD.type, type);
		}
	};

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			position="right"
			className={className}
			transitionProps={drawerTransitionProps}
			trapFocus={false}
			title={
				<Title order={4} fw={600}>
					{tCapManage('holdings.event.drawer_add')}
				</Title>
			}
		>
			{createHoldingsEventRequest.isPending && <InlineLoader />}
			<FormProvider {...createHoldingsEventForm}>
				<form
					onSubmit={createHoldingsEventForm.handleSubmit(onSubmit())}
					style={{
						height: '100%',
						display: 'flex',
						flexDirection: 'column',
						gap: '1rem',
					}}
				>
					<ParentDiv>
						<HoldingCard>
							<SelectTitle>{tCapManage('holdings.event.select_type')}</SelectTitle>
							<HoldingsEventTabs>
								{HOLDINGS_EVENT_TYPE_OPTIONS.map((option) => {
									const isDisabled = hasEventType(
										existingEvents,
										option.value as HoldingEventType
									);
									return (
										<HoldingsEventTab
											key={option.value}
											selected={selectedType === option.value}
											disabled={isDisabled}
											onClick={() => handleTabSelect(option.value)}
										>
											{option.label}
										</HoldingsEventTab>
									);
								})}
							</HoldingsEventTabs>
						</HoldingCard>

						<InfoCard>
							<MantineDateInput
								name={CREATE_HOLDING_EVENT_FORM_FIELD.date}
								label={dateLabel}
								placeholder={tCapManage('holdings.event.select_date')}
								size="md"
								onChange={(value) => {
									const isoDate = browserDateToMidnightUTC(value);
									if (!isoDate) {
										return;
									}

									createHoldingsEventForm.setValue(
										CREATE_HOLDING_EVENT_FORM_FIELD.date,
										isoDate
									);
								}}
							/>

							{selectedType === HoldingEventType.PLEDGING_OF_SHARES && (
								<>
									<HoldingEntitySelect
										name={CREATE_HOLDING_EVENT_FORM_FIELD.pledgeToString}
										personIdFieldName={
											CREATE_HOLDING_EVENT_FORM_FIELD.pledgeToPersonId
										}
										label={tCapManage('holdings.event.pledge_to')}
										placeholder={tCapManage('holdings.event.pledge_person')}
										options={localEntitiesOptions}
										setOptions={setLocalEntitiesOptions}
									/>
									<PledgedAgainstSelect
										debtHoldings={debtHoldings}
										formFields={{
											pledgeFromString:
												CREATE_HOLDING_EVENT_FORM_FIELD.pledgeFromString,
											pledgeFromHolderId:
												CREATE_HOLDING_EVENT_FORM_FIELD.pledgeFromHolderId,
											pledgeFromSecurityId:
												CREATE_HOLDING_EVENT_FORM_FIELD.pledgeFromSecurityId,
										}}
									/>
								</>
							)}

							{selectedType === HoldingEventType.ASSIGNMENT_OF_VOTING_RIGHTS && (
								<HoldingEntitySelect
									name={CREATE_HOLDING_EVENT_FORM_FIELD.assignedToString}
									personIdFieldName={
										CREATE_HOLDING_EVENT_FORM_FIELD.assignedToPersonId
									}
									label={tCapManage('holdings.event.assigned_to')}
									placeholder={tCapManage('holdings.event.pledge_person')}
									options={localEntitiesOptions}
									setOptions={setLocalEntitiesOptions}
								/>
							)}

							<Textarea
								{...createHoldingsEventForm.register(
									CREATE_HOLDING_EVENT_FORM_FIELD.note
								)}
								label={tCapManage('holdings.event.note_to')}
								placeholder={tCapManage('holdings.event.note_desc')}
								size="md"
								autosize
								minRows={4}
							/>

							<UnitsTitle>{tCapManage('holdings.event.document')}</UnitsTitle>
							{!uploadedFile ? (
								<FileUploadComponent
									setUploadedFile={setUploadedFile}
									inputId="fileUpload"
								/>
							) : (
								<FilePreview
									fileName={uploadedFile?.name || 'Holding file'}
									onRemove={removeFile}
								/>
							)}
						</InfoCard>
					</ParentDiv>

					<Button
						fullWidth
						mt="auto"
						type="submit"
						disabled={availableEventTypes.length === 0}
					>
						{tCapManage('holdings.event.create')}
					</Button>
					<Button fullWidth variant="default" onClick={() => onClose()}>
						{tCapManage('holdings.event.cancel')}
					</Button>
				</form>
			</FormProvider>
		</Drawer>
	);
};
